/**
 * CorporateProfilePage — V2 orchestrator for the Corporate profile.
 *
 * Manages:
 *   - Session & permissions
 *   - Single polling cycle (600s) for corporation details
 *   - Tab state (basic info — single tab, expandable later)
 *   - Drawer/dialog state for Edit, Add Campus, Impersonate flows
 *   - Toast notifications
 *
 * Props:
 *   id — Corporate ID (from URL param)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './corporate-profile-page.css';
import NavigationV2 from '../../../../components/v2/NavigationV2.jsx';
import Toast from '../../../../components/v2/Toast.jsx';
import useToast from '../../../../hooks/useToast.js';
import ProfileBanner from '../../../../components/v2/ProfileBanner.jsx';
import BasicInfoTab from './components/BasicInfoTab.jsx';
import BasicInfoDrawer from './components/BasicInfoDrawer.jsx';
import AddCampusDrawer from './components/AddCampusDrawer.jsx';

import {
  getCampusId, getUserId, getSessionId, getMyRoleWeight,
} from '../../../../services/session.js';
import {
  fetchCorporation, fetchTimezones, fetchUserRoles,
  editCorporation, addCampus,
} from './api/corporateProfileApi.js';
import {
  iAmHigherAuthority as iAmHigherAuth,
  formatPhone,
} from '../../../../libs.jsx';
import { impersonationRoles, startImpersonation } from '../../../../utils/impersonation.js';
import ImpersonateDialog from '../../../../components/v2/ImpersonateDialog.jsx';

// ==========================================================================
// COMPONENT
// ==========================================================================

const CorporateProfilePage = ({ id }) => {
  // --------------------------------------------------------------------------
  // SESSION
  // --------------------------------------------------------------------------
  const campusId = useMemo(() => getCampusId(), []);
  /* The user's OWN weight (my_role_weight), not the impersonated role_weight: the same weight
     Profiles.jsx gates this page on (> 99). Impersonating into a community as a lower role changes role_weight,
     which used to take away this page's rights while the page itself stayed open. */
  const myRoleWeight = useMemo(() => getMyRoleWeight(), []);
  const editRights = useMemo(() => iAmHigherAuth(80, myRoleWeight), [myRoleWeight]);
  const addRights = useMemo(() => iAmHigherAuth(80, myRoleWeight), [myRoleWeight]);

  const corpId = useMemo(() => (id ? parseInt(id, 10) : 0), [id]);

  const getSessionParams = useCallback(() => ({
    campusId,
    userId: getUserId(),
    clientId: getSessionId(),
  }), [campusId]);

  // --------------------------------------------------------------------------
  // TOAST
  // --------------------------------------------------------------------------
  const { toast, showToast, dismissToast } = useToast(6000);

  // --------------------------------------------------------------------------
  // DATA STATE
  // --------------------------------------------------------------------------
  const [corpData, setCorpData] = useState(null);
  const [timezones, setTimezones] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [activeTab, setActiveTab] = useState('basic');
  const [basicDrawerOpen, setBasicDrawerOpen] = useState(false);
  const [addCampusDrawerOpen, setAddCampusDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Impersonate
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const [impersonateRoleWeight, setImpersonateRoleWeight] = useState(myRoleWeight);
  const [impersonating, setImpersonating] = useState(false);

  // --------------------------------------------------------------------------
  // DATA LOADING
  // --------------------------------------------------------------------------

  /** Fetch corporation data (runs on mount + every 600s) */
  const fetchCorpDataCycle = useCallback(async () => {
    const result = await fetchCorporation(campusId, corpId);
    if (result && result.length > 0) {
      setCorpData(result[0]);
    }
    setLoading(false);
  }, [campusId, corpId]);

  /** Fetch support data (timezones + roles) — once on mount */
  const fetchSupportData = useCallback(async () => {
    const [tzResult, rolesResult] = await Promise.all([
      fetchTimezones(campusId),
      fetchUserRoles(campusId),
    ]);
    setTimezones(tzResult || []);
    setUserRoles(rolesResult || []);
  }, [campusId]);

  useEffect(() => {
    fetchCorpDataCycle();
    fetchSupportData();
    const interval = setInterval(fetchCorpDataCycle, 600_000);
    return () => clearInterval(interval);
  }, [fetchCorpDataCycle, fetchSupportData]);

  // --------------------------------------------------------------------------
  // HANDLERS
  // --------------------------------------------------------------------------

  /** Save edited basic info */
  const handleBasicInfoSave = useCallback(async (formData) => {
    setSaving(true);
    const session = getSessionParams();

    const payload = [{
      corporate_id: corpId,
      corporate_name: formData.corpName,
      corporate_guid: formData.corpGuid,
      country: formData.country,
      state: formData.state,
      city: formData.city,
      zip: formData.zipcode,
      email: formData.email,
      phone: formData.phone,
      modified_by: session.userId,
      street_address: formData.streetAddress,
    }];

    const oldPayload = [{
      corporate_id: corpId,
      corporate_name: corpData?.corporate_name,
      corporate_guid: corpData?.corporate_guid,
      country: corpData?.country,
      state: corpData?.state,
      city: corpData?.city,
      zip: corpData?.zip || corpData?.zipcode,
      email: corpData?.email,
      phone: corpData?.phone,
      street_address: corpData?.street_address,
    }];

    const result = await editCorporation(payload, oldPayload, session);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', 'Corporation information saved.');
      setBasicDrawerOpen(false);
      fetchCorpDataCycle();
    } else {
      showToast('error', `Error during save${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [corpId, corpData, getSessionParams, showToast, fetchCorpDataCycle]);

  /** Add campus */
  const handleAddCampus = useCallback(async (campusData) => {
    const session = getSessionParams();
    const [success, errMsg] = await addCampus(campusData, session);

    if (success) {
      showToast('success', `${campusData.campus_name} has been added.`);
      setAddCampusDrawerOpen(false);
      fetchCorpDataCycle();
    } else {
      showToast('error', `Failed to add campus${errMsg ? ': ' + errMsg : ''}.`);
    }
  }, [getSessionParams, showToast, fetchCorpDataCycle]);

  /** Impersonate */
  const handleConfirmImpersonate = useCallback(() => {
    if ((corpData?.campuses || []).length === 0) {
      showToast('error', 'No campuses to impersonate');
      return;
    }
    const err = startImpersonation({ corporateId: corpId, roleWeight: impersonateRoleWeight, userRoles });
    if (err) {
      showToast('error', err);
      return;
    }
    setImpersonating(true);
  }, [corpData, corpId, impersonateRoleWeight, userRoles, showToast]);

  /** Filtered role options for impersonate dialog */
  const filteredRoles = useMemo(() => impersonationRoles(userRoles, myRoleWeight), [userRoles, myRoleWeight]);

  // --------------------------------------------------------------------------
  // DERIVED VALUES
  // --------------------------------------------------------------------------
  const corpName = corpData?.corporate_name || '';
  const campuses = corpData?.campuses || [];

  const bannerMeta = useMemo(() => [
    { label: 'Email', value: corpData?.email || '—' },
    { label: 'Phone', value: corpData?.phone ? formatPhone(corpData.phone) : '—' },
    {
      label: 'Location',
      value: [corpData?.city, corpData?.state].filter(Boolean).join(', ') || '—',
    },
  ], [corpData]);

  // --------------------------------------------------------------------------
  // TAB LIST
  // --------------------------------------------------------------------------
  const tabs = useMemo(() => [
    { key: 'basic', label: 'Basic Info' },
  ], []);

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <>
      <NavigationV2 />
      <div className="nav2-main">
      <div className="ps-page">
        {/* Toast */}
        <Toast toast={toast} onDismiss={dismissToast} />

        {/* Page Header */}
        <header className="ps-page-header">
          <div className="ps-page-header__inner">
            <div className="ps-breadcrumb-row">
              <nav className="ps-breadcrumb">
                <Link to="/customers/corporations" className="ps-breadcrumb__text">Corporations</Link>
                <span className="ps-breadcrumb__separator">/</span>
                <span className="ps-breadcrumb__current">{corpName || 'Corporate Profile'}</span>
              </nav>
              <div className="crpp-header-actions">
                {addRights && (
                  <button
                    type="button"
                    className="dp-btn dp-btn--secondary dp-btn--sm"
                    onClick={() => {
                      if (campuses.length === 0) {
                        showToast('error', 'No campuses to impersonate');
                        return;
                      }
                      setImpersonateOpen(true);
                      setImpersonateRoleWeight(myRoleWeight);
                      setImpersonating(false);
                    }}
                  >
                    <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Impersonate
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Profile Banner */}
        <ProfileBanner
          name={corpName}
          subtitle={`Corp ID #${corpId}`}
          metaFields={bannerMeta}
          loading={loading}
        />

        {/* Tab Bar */}
        <div className="pp-tab-bar">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`pp-tab${activeTab === tab.key ? ' is-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="pp-tab-content">
          {activeTab === 'basic' && (
            <BasicInfoTab
              corpData={corpData}
              editRights={editRights}
              addRights={addRights}
              onEdit={() => setBasicDrawerOpen(true)}
              onAddCampus={() => setAddCampusDrawerOpen(true)}
            />
          )}
        </div>

        {/* Basic Info Drawer (Edit Corporation) */}
        {basicDrawerOpen ? (
          <div className="dp-drawer-backdrop" onClick={() => setBasicDrawerOpen(false)} aria-hidden="true" />
        ) : null}
        <BasicInfoDrawer
          open={basicDrawerOpen}
          onClose={() => setBasicDrawerOpen(false)}
          onSave={handleBasicInfoSave}
          saving={saving}
          corpData={corpData}
        />

        {/* Add Campus Drawer */}
        <AddCampusDrawer
          open={addCampusDrawerOpen}
          onClose={() => setAddCampusDrawerOpen(false)}
          onSave={handleAddCampus}
          corporateId={corpId}
          corporateName={corpName}
          timezones={timezones}
        />

        {/* Impersonate Dialog */}
        {impersonateOpen ? (
          <ImpersonateDialog
            subject={corpName}
            detail={campuses.length > 0 ? campuses.map((c) => c.campus_name).join(', ') : null}
            roles={filteredRoles}
            myRoleWeight={myRoleWeight}
            roleWeight={impersonateRoleWeight}
            onRoleChange={setImpersonateRoleWeight}
            busy={impersonating}
            onCancel={() => setImpersonateOpen(false)}
            onConfirm={handleConfirmImpersonate}
          />
        ) : null}

      </div>
      </div>
    </>
  );
};

export default CorporateProfilePage;
